/**
 * Credit reads and spending, with the out-of-credits modal wired in.
 *
 * This is where the credits domain meets the entitlements domain: Unlimited is an
 * *entitlement*, but it changes how credits behave. Rather than have creditsStore
 * reach into entitlementsStore, the two are composed here.
 *
 * SIMULATED. Balances are client-side and reset on reload; nothing is charged.
 */
import { useEffect } from 'react';
import { ApiError } from '../api/errors';
import { availableCredits } from '../lib/credits';
import { useCreditsStore } from '../stores/creditsStore';
import { useEntitlementsStore } from '../stores/entitlementsStore';
import { useUiStore } from '../stores/uiStore';
import type { CreditBalance } from '../types/domain';

/**
 * Keeps the credit model's `unlimited` flag in sync with the entitlement.
 *
 * Call once, high in the tree. Without this the two domains could disagree — the
 * account would own Unlimited while the meter still metered.
 */
export function useSyncUnlimited(): void {
  const unlimited = useEntitlementsStore((s) => s.unlimited);
  const setUnlimited = useCreditsStore((s) => s.setUnlimited);

  useEffect(() => {
    setUnlimited(unlimited);
  }, [unlimited, setUnlimited]);
}

export function useCredits() {
  // Each field is selected separately rather than returning one object. Zustand
  // compares selector results with Object.is, so an object literal would be a new
  // reference on every store change and this hook would re-render its consumers
  // on any credit update, related or not.
  const total = useCreditsStore((s) => s.total);
  const reserved = useCreditsStore((s) => s.reserved ?? 0);
  const used = useCreditsStore((s) => s.used);
  const unlimited = useCreditsStore((s) => s.unlimited);
  const monthlyAllowance = useCreditsStore((s) => s.monthlyAllowance ?? 0);
  const monthlyUsed = useCreditsStore((s) => s.monthlyUsed ?? 0);
  const topUp = useCreditsStore((s) => s.topUp ?? 0);
  const topUpUsed = useCreditsStore((s) => s.topUpUsed ?? 0);
  const resetsAt = useCreditsStore((s) => s.resetsAt ?? null);
  const unlimitedExpiresAt = useCreditsStore((s) => s.unlimitedExpiresAt ?? null);
  const balance: CreditBalance = { total, reserved, used, unlimited };
  const spendRaw = useCreditsStore((s) => s.spend);
  const grant = useCreditsStore((s) => s.grant);
  const canAfford = useCreditsStore((s) => s.canAfford);
  const setOutOfCreditsOpen = useUiStore((s) => s.setOutOfCreditsOpen);

  return {
    /** Credits still available on a finite account. */
    credits: availableCredits(balance),
    reserved,
    used: balance.used,
    total: balance.total,
    isUnlimited: balance.unlimited,
    monthlyAllowance,
    monthlyUsed,
    topUp,
    topUpUsed,
    resetsAt,
    unlimitedExpiresAt,
    canAfford,
    grant,

    /**
     * Spends credits, raising the out-of-credits modal and returning false when the spend
     * did not happen.
     *
     * Callers must branch on the return value: `if (!(await spend(2, 'Remix'))) return;`.
     * Every credit-consuming path in the app goes through here, so the modal cannot be
     * forgotten at one call site the way it could when each feature checked the balance
     * itself.
     *
     * AWAITED NOW, because the store's spend is. `false` covers both refusals — a balance
     * the client can see is short, and a spend the API rejected — and the modal is the right
     * answer to both: the second is a `402`, which is the server saying the same thing.
     * The rollback happens in the store; nothing here has to unwind anything.
     */
    async spend(amount: number, reason: string): Promise<boolean> {
      try {
        if (await spendRaw(amount, reason)) return true;
      } catch (err) {
        if (err instanceof ApiError && err.code !== 'insufficient_credits') throw err;
        setOutOfCreditsOpen(true);
        return false;
      }
      setOutOfCreditsOpen(true);
      return false;
    },
  };
}
